import * as React from 'react';
import { 
    Typography, 
    Box, 
    Container, 
    Button, 
    Card, 
    Grid, 
    CardContent, 
    Paper,
    Skeleton
} from '@mui/material';
import { connect } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import { main_button } from '../../app/components/Styles';
import { useEffect } from 'react';
import { getOrders, getProducts, getDashboard } from '../../app/store/actions/imsAdminActions';
import { 
    AccountCircle, 
    Inventory, 
    LocalShipping, 
    MoveToInbox, 
    Notifications, 
    Outbox, 
    People, 
    Receipt, 
    Store, 
    Warehouse, 
    Warning,
    Assessment,
    ShoppingCart,
    CheckCircle,
    TrendingUp,
    FactCheck
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { BrowserView } from 'react-device-detect';

const IMSAdminDashboardSkeleton = () => (
    <>
        <Grid container spacing={2} sx={{ mb: 4 }}>
            {Array.from({ length: 8 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                    <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Skeleton variant="text" width="60%" height={22} animation="wave" />
                            <Skeleton variant="text" width="40%" height={48} animation="wave" />
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Skeleton variant="text" width="55%" height={32} animation="wave" />
                            <Skeleton variant="circular" width={32} height={32} animation="wave" />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 3 }}>
                            <Skeleton variant="rounded" height={100} sx={{ flex: 1 }} animation="wave" />
                            <Skeleton variant="rounded" height={100} sx={{ flex: 1 }} animation="wave" />
                        </Box>
                        <Skeleton variant="rounded" width="100%" height={48} animation="wave" />
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Skeleton variant="text" width="65%" height={24} animation="wave" />
                            <Skeleton variant="circular" width={32} height={32} animation="wave" />
                        </Box>
                        <Skeleton variant="text" width="45%" height={56} sx={{ mb: 1 }} animation="wave" />
                        <Skeleton variant="text" width="50%" height={22} animation="wave" />
                    </CardContent>
                </Card>
            </Grid>
        </Grid>

        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Skeleton variant="rounded" width={40} height={40} animation="wave" />
                        <Skeleton variant="text" width="45%" height={28} animation="wave" />
                    </Box>
                    <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', display: 'block' }} animation="wave" />
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Skeleton variant="text" width="30%" height={40} sx={{ mx: 'auto' }} animation="wave" />
                        <Skeleton variant="text" width="35%" height={22} sx={{ mx: 'auto', mt: 1 }} animation="wave" />
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Skeleton variant="rounded" width={40} height={40} animation="wave" />
                        <Skeleton variant="text" width="40%" height={28} animation="wave" />
                    </Box>
                    <Skeleton variant="rounded" width="100%" height={300} animation="wave" />
                </Paper>
            </Grid>
        </Grid>
    </>
);

export const IMSAdminHome = ({ getDashboard, dashboard, user, isLoading }) => {
    const navigate = useNavigate();
    const eventId = useParams().id;

    useEffect(() => {
        getDashboard();
    }, [getDashboard]);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header with Navigation Buttons */}
            <Paper 
                elevation={2}
                sx={{ 
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    backgroundColor: 'white'
                }}
            >
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: 1.5,
                            backgroundColor: 'var(--secColor)',
                            color: 'white'
                        }}
                    >
                        <Assessment color="white" sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant='h4' sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                        {user?.store?.name || 'Store Dashboard'}
                    </Typography>
                </Box>

                <BrowserView>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/action-center")}
                                startIcon={<Warning />}
                            >
                                Action Center
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/bazaars")}
                                startIcon={<Store />}
                            >
                                Bazaars
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/brands")}
                                startIcon={<Store />}
                            >
                                Brands
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/orders")}
                                startIcon={<Receipt />}
                            >
                                Orders
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/products")}
                                startIcon={<Inventory />}
                            >
                                Products
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/warehouses")}
                                startIcon={<Warehouse />}
                            >
                                Warehouses
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/inbounds")}
                                startIcon={<MoveToInbox />}
                            >
                                Inbounds
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/outbounds/create")}
                                startIcon={<Outbox />}
                            >
                                Outbounds
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/item-updates")}
                                startIcon={<FactCheck />}
                            >
                                Item updates
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/employees")}
                                startIcon={<People />}
                            >
                                Employees
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/notifications")}
                                startIcon={<Notifications />}
                            >
                                Notifications
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/admin/shipping-services")}
                                startIcon={<LocalShipping />}
                            >
                                Shipping Services
                            </Button>
                        </Grid>
                    </Grid>
                </BrowserView>
            </Paper>

            {isLoading ? (
                <IMSAdminDashboardSkeleton />
            ) : (
            <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Delayed, in-transit, and no-shipping counts include records from 1 June 2026 onward. Partially received inbounds are not delayed.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {(() => {
                    const ops = dashboard?.opsAnalysis || {};
                    const tiles = [
                        { key: "piecesInWarehouse", label: "Pieces in warehouse", value: ops.piecesInWarehouse, hot: false, to: "/ims/admin/products" },
                        { key: "delayedOrderSla", label: "Delayed Order SLA", value: ops.delayedOrderSla, hot: true, to: "/ims/admin/orders?ops=delayedSla" },
                        { key: "delayedTickets", label: "Delayed Tickets", value: ops.delayedTickets, hot: true, to: "/ims/admin/tickets?ops=delayed" },
                        { key: "beforeNoon", label: "Confirmed before 12:00", value: ops.beforeNoon, hot: false, to: "/ims/admin/orders?ops=beforeNoon" },
                        { key: "delayedInbounds", label: "Delayed Inbounds", value: ops.delayedInbounds, hot: true, to: "/ims/admin/inbounds?ops=delayed" },
                        { key: "delayedReturns", label: "Delayed Returns", value: ops.delayedReturns, hot: true, to: "/ims/admin/action-center?tab=lateReturns" },
                        { key: "noShipping", label: "No Shipping", value: ops.noShipping, hot: true, to: "/ims/admin/action-center?tab=noShipping" },
                        { key: "inTransit", label: "In Transit", value: ops.inTransit, hot: false, to: "/ims/admin/orders?ops=inTransit" },
                    ];
                    return tiles.map((tile) => {
                        const isHot = tile.hot && (tile.value || 0) > 0;
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={tile.key}>
                                <Card
                                    elevation={0}
                                    onClick={() => navigate(tile.to)}
                                    sx={{
                                        borderRadius: 3,
                                        height: "100%",
                                        cursor: "pointer",
                                        backgroundColor: "var(--card-solid, #fff)",
                                        border: isHot ? "1px solid rgba(190,50,62,0.35)" : "1px solid var(--s10, rgba(0,0,0,0.08))",
                                        color: isHot ? "var(--red-tx2, #be323e)" : "var(--tx, inherit)",
                                        "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
                                    }}
                                >
                                    <CardContent sx={{ py: 2.5 }}>
                                        <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.75, fontWeight: 500 }}>
                                            {tile.label}
                                        </Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                            {Number(tile.value || 0).toLocaleString()}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    });
                })()}
            </Grid>

            {/* Dashboard Statistics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Orders to Pack & Ship Combined Card */}
                <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6" sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                                    Orders to Pack & Ship
                                </Typography>
                                <ShoppingCart sx={{ color: 'var(--secColor)', fontSize: 32 }} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 3 }}>
                                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>
                                        To Pack
                                    </Typography>
                                    <Typography variant="h3" sx={{ color: "#2196F3", fontWeight: 600 }}>
                                        {dashboard?.ordersLeftToPack || 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>
                                        To Ship
                                    </Typography>
                                    <Typography variant="h3" sx={{ color: "#FF9800", fontWeight: 600 }}>
                                        {dashboard?.ordersLeftToSend || 0}
                                    </Typography>
                                </Box>
                            </Box>
                            <Button 
                                fullWidth
                                variant="contained" 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate('/ims/admin/packing')}
                            >
                                Go to Packing System
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Orders This Month Card */}
                <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Total Orders This Month
                                </Typography>
                                <TrendingUp sx={{ color: 'var(--secColor)', fontSize: 32 }} />
                            </Box>
                            <Typography variant="h2" sx={{ color: "var(--secColor)", fontWeight: 600, mb: 1 }}>
                                {dashboard?.totalOrdersThisMonth || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Orders processed this month
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3}>
                {/* Success Rate Pie Chart */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1,
                                    backgroundColor: 'var(--secColor)',
                                    color: 'white'
                                }}
                            >
                                <CheckCircle color="white" sx={{ fontSize: 24 }} />
                            </Box>
                            <Typography variant="h6" sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                                Order Success Rate
                            </Typography>
                        </Box>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'Success', value: dashboard?.deliveredOrdersThisMonth || 0 },
                                        { name: 'Failure', value: dashboard?.rtoOrdersThisMonth || 0 },
                                    ]}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    label
                                >
                                    <Cell key="success" fill="#4CAF50" />
                                    <Cell key="failure" fill="#E45417" />
                                </Pie>
                                <Legend />
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Typography variant="h4" sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                                {dashboard?.successRate?.toFixed(2) || 0}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Success Rate
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Top Brands Bar Chart */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1,
                                    backgroundColor: 'var(--secColor)',
                                    color: 'white'
                                }}
                            >
                                <Store color="white" sx={{ fontSize: 24 }} />
                            </Box>
                            <Typography variant="h6" sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                                Top Brands
                            </Typography>
                        </Box>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={(dashboard?.topVendors || []).map(([name, value]) => ({ name, value }))}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="name" 
                                    angle={-45}
                                    textAnchor="end"
                                    height={100}
                                    interval={0}
                                />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="value" fill="var(--secColor)" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>
            </>
            )}
        </Container>
    );
}

const mapStateToProps = (state) => ({
    store: state?.records?.store,
    dashboard: state?.records?.dashboard,
    user: state?.auth?.user,
    isLoading: state?.records?.isLoading || state?.wait?.isLoading
});

const mapDispatchToProps = { getOrders, getProducts, getDashboard };

export default connect(mapStateToProps, mapDispatchToProps)(IMSAdminHome);
